import sfxMetadata from "audio/sfx-metadata.json";
import sfxUrl from "audio/sfx.mp3";
import { RunnerWithLock } from "client-util/runnerWithLock";
import { Howl, Howler } from "howler";
import { useEffect } from "react";
import {
  HuntNotification,
  HuntNotificationType,
} from "sockapi/client-notifications";
import { AdminQueueTaskType } from "sockapi/scope-specs/admin-queue-task-spec";
import { useBrowserWidePersistentStateStore } from "stores/BrowserWidePersistentState";
import { useScopeStateStore } from "stores/ScopeStateStore";
import { useSeparatelyPersistedState } from "stores/UserPreferencesStore";

// hacky way to make an enum
export type SFX = Uppercase<keyof (typeof sfxMetadata)["sprite"]>;
export const SFX = Object.fromEntries(
  Object.keys(sfxMetadata["sprite"]).map((k) => [k.toUpperCase(), k])
) as { [key in SFX]: string };

class SFXPlayerTemplate {
  howl: Howl;
  runner: RunnerWithLock;
  /** Previous play IDs to prevent overlap. */
  prevPlayIds: { [type in HuntNotificationType]?: number };

  constructor() {
    this.howl = new Howl({
      src: [sfxUrl],
      // @ts-expect-error: types aren't quite inferred right
      sprite: sfxMetadata.sprite,
    });
    this.runner = new RunnerWithLock();
    this.prevPlayIds = {};
  }

  play(
    sfxId: (typeof SFX)[SFX],
    {
      notifType,
      stopOtherSfx = true,
    }: {
      notifType?: HuntNotificationType;
      stopOtherSfx?: boolean;
    } = {}
  ) {
    if (notifType) {
      void this.runner.run({ key: notifType }, async () => {
        const { promise, resolve } = Promise.withResolvers<void>();
        if (stopOtherSfx) {
          const prevPlayId = this.prevPlayIds[notifType];
          if (prevPlayId) {
            this.howl.stop();
          }
        }
        const playId = this.howl.play(sfxId);
        this.prevPlayIds[notifType] = playId;
        this.howl.once("end", () => resolve(), playId);
        return promise;
      });
    } else {
      if (stopOtherSfx) {
        this.howl.stop();
      }
      this.howl.play(sfxId);
    }
  }

  teardown() {
    this.howl.unload();
  }

  handleNotif(notif: HuntNotification) {
    const playSfx = (sfxId: string, stopOtherSfx?: boolean) => {
      this.play(sfxId, { notifType: notif.type, stopOtherSfx });
    };
    const pinnedPuzzles =
      useBrowserWidePersistentStateStore.getState().state.pinnedPuzzles ?? [];

    switch (notif.type) {
      case HuntNotificationType.ADMIN_QUEUE_TASK:
        if (
          notif.taskSummary.type === AdminQueueTaskType.HINT &&
          pinnedPuzzles.includes(notif.taskSummary.puzName)
        ) {
          playSfx(SFX["ROCK-FLOPSTER"]);
          break;
        }
        playSfx(SFX["NOTIF-WAVE"]); // peace for yannick
        break;
      case HuntNotificationType.SUBMISSION_REJECTED:
      case HuntNotificationType.HINT_ANSWERED: {
        playSfx(SFX["NOTIF-BOSSA-A"]); // chimes-sad motif
        break;
      }
      case HuntNotificationType.ERRATUM: {
        playSfx(SFX["ROCK-FLOPSTER"]); // we won't have any errata right? feel free to play this elsewhere
        break;
      }
      case HuntNotificationType.UNLOCK: {
        playSfx(SFX["NOTIF-BOSSA-B"]); // chimes motif
        break;
      }
      case HuntNotificationType.SOLVE: {
        playSfx(SFX["NOTIF-CLAPS"]);
        break;
      }
      case HuntNotificationType.VICTORY: {
        playSfx(SFX["NOTIF-CHEER"]);
        playSfx(SFX["NOTIF-BOSSA-AB"], false);
        break;
      }
    }
  }
}

export const SFXPlayer = new SFXPlayerTemplate();

export const useAudioEffect = () => {
  const volume = useSeparatelyPersistedState((state) => state.volume);
  const isMuted = useSeparatelyPersistedState((state) => state.isMuted);
  const setPlayAudioNotif = useScopeStateStore(
    (state) => state.setPlayAudioNotif
  );

  useEffect(() => {
    Howler.volume(volume / 100);
  }, [volume, isMuted]);

  useEffect(() => {
    Howler.mute(isMuted);
  }, [isMuted]);

  // notif layer
  useEffect(() => {
    setPlayAudioNotif((upd) => {
      SFXPlayer.handleNotif(upd);
    });

    return () => {
      setPlayAudioNotif(null);
    };
  }, [setPlayAudioNotif]);
};
